import { ThemeOptions } from '@mui/material';
import MuiChip, { ChipProps as MuiChipProps } from '@mui/material/Chip';
import * as React from 'react';

import { IconButton } from '../button';
import { useMergeRefs, useOverflow } from '../hooks';
import { styled } from '../styled';
import { ColorProp } from '../theme';
import { ChipColor } from '../theme/manifest';
import { Tooltip } from '../tooltip';
import { Typography } from '../typography';
import { enumType, makeEnum, withVersion } from '../utils';

export * from './chip-group';
export * from './birthday-chip';
export * from './chip-variants';

const sizes = ['small', 'medium'] satisfies MuiChipProps['size'][];

export const ChipSize = makeEnum(...sizes);
export type ChipSize = enumType<typeof ChipSize>;

interface ChipBaseProps
    extends TypedOmit<
        MuiChipProps,
        'color' | 'size' | 'onClick' | 'onDelete' | 'disabled' | 'clickable'
    > {
    /** @default default */
    color?: ChipColor;
    /** @default small */
    size?: MuiChipProps['size'];
    /** @default 150 */
    maxWidth?: number | 'none';
    tooltipText?: string;
}

interface ReadOnlyChipProps extends ChipBaseProps {
    onClick?: never;
    onDelete?: never;
    selected?: never;
    disabled?: never;
}

interface RemovableChipProps extends ChipBaseProps {
    onDelete: NonNullable<MuiChipProps['onDelete']>;
    onClick?: never;
    selected?: never;
    disabled?: never;
}

interface SelectableChipProps extends ChipBaseProps {
    onClick: NonNullable<MuiChipProps['onClick']>;
    selected?: boolean;
    onDelete?: never;
    disabled?: boolean;
}

export type ChipProps =
    | ReadOnlyChipProps
    | RemovableChipProps
    | SelectableChipProps;

const ChipImpl = React.forwardRef<HTMLDivElement, ChipProps>((props, ref) => {
    const {
        color = 'default',
        maxWidth = 150,
        size = 'small',
        label,
        onDelete,
        tooltipText,
        selected,
        ...muiProps
    } = props;
    const innerRef = useMergeRefs(ref);
    const labelRef = React.useRef<HTMLSpanElement | null>(null);
    const { textOverflow } = useOverflow(labelRef, props.label);

    const isRemovable = Boolean(onDelete);
    const isSelectable = Boolean(muiProps.onClick) && !isRemovable;
    const resolvedDisabled = isSelectable ? muiProps.disabled : undefined;
    const isSelected = isSelectable && selected && !resolvedDisabled;
    const resolvedColor = isSelectable ? 'selectable' : color;

    const colorMain = getChipColor(resolvedColor, 'main');
    const colorHover = getChipColor(resolvedColor, 'hovered');
    const colorFocused = getChipColor(resolvedColor, 'focused');
    const colorActive = getChipColor(resolvedColor, 'active');

    const bgColor = isSelected ? colorActive : colorMain;
    const labelColor = getChipLabelColor(resolvedColor, isSelected);

    const hasSelectableInteraction = Boolean(!resolvedDisabled && isSelectable);
    const resolvedCursor = hasSelectableInteraction ? 'pointer' : 'default';
    const { onClick, ...forwardedMuiProps } = muiProps;

    return (
        <Tooltip
            title={tooltipText ?? props.label}
            bypass={tooltipText ? false : !textOverflow}
        >
            <MuiChip
                ref={innerRef}
                label={
                    label ? (
                        isRemovable ? (
                            <RemovableLabel>
                                {typeof label === 'string' ? (
                                    <Label ref={labelRef}>{label}</Label>
                                ) : (
                                    label
                                )}
                                <IconButton
                                    icon="IconClose"
                                    size="smaller"
                                    aria-label="delete"
                                    onClick={onDelete}
                                />
                            </RemovableLabel>
                        ) : typeof label === 'string' ? (
                            <Label ref={labelRef}>{label}</Label>
                        ) : (
                            label
                        )
                    ) : undefined
                }
                data-role="chip"
                size={size}
                {...forwardedMuiProps}
                onClick={isSelectable ? onClick : undefined}
                disabled={resolvedDisabled}
                color="default"
                sx={{
                    backgroundColor: bgColor,
                    color: labelColor,
                    cursor: resolvedCursor,
                    borderRadius: 1,
                    '.MuiChip-label': label
                        ? {
                              boxSizing: 'content-box',
                              maxWidth
                          }
                        : {
                              padding: 0
                          },
                    ...(hasSelectableInteraction && {
                        '&.MuiChip-clickable:hover': {
                            backgroundColor: isSelected ? bgColor : colorHover,
                            color: isSelected
                                ? labelColor
                                : getChipColor(resolvedColor, 'contrastText')
                        },
                        '&.MuiChip-clickable.Mui-focusVisible': {
                            backgroundColor: colorFocused
                        },
                        '&.MuiChip-clickable:active': {
                            backgroundColor: colorFocused
                        }
                    }),
                    ...muiProps.sx
                }}
            />
        </Tooltip>
    );
});

export const Chip = withVersion(ChipImpl, '1.2.0');

(Chip as React.FunctionComponent).displayName = 'Chip';

interface LabelProps {
    children: string;
}

const Label = React.forwardRef<HTMLSpanElement, LabelProps>((props, ref) => {
    return (
        <Typography
            component="span"
            ref={ref}
            overflow="hidden"
            textOverflow="ellipsis"
            display="block"
            sx={{
                font: 'inherit',
                lineHeight: 'inherit',
                letterSpacing: 'inherit'
            }}
        >
            {props.children}
        </Typography>
    );
});

const RemovableLabel = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    maxWidth: '100%'
});

function getChipColor(
    color: ChipColor,
    token:
        | 'main'
        | 'contrastText'
        | 'hovered'
        | 'focused'
        | 'active'
        | 'contrast-text-active'
): ColorProp {
    return `components.chip.${color}.${token}` as ColorProp;
}

/** Returns the label ColorProp for the chip — switches to the active contrast token when selected. */
function getChipLabelColor(
    color: ChipColor,
    isSelected: boolean | undefined
): ColorProp {
    return isSelected
        ? getChipColor('selectable', 'contrast-text-active')
        : getChipColor(color, 'contrastText');
}

export const chipStyle = {
    MuiChip: {
        styleOverrides: {
            sizeSmall: ({ theme }) => ({
                height: 24,
                fontSize: theme.palette.components?.chip?.small?.fontSize,
                fontWeight: theme.palette.components?.chip?.small?.fontWeight,
                '& .MuiChip-icon': {
                    marginLeft: theme.spacing(0.5),
                    marginRight: theme.spacing(0.5)
                },
                '& .MuiChip-label': {
                    paddingLeft: theme.spacing(0.5),
                    paddingRight: theme.spacing(0.5)
                },
                '& .MuiChip-deleteIcon': {
                    padding: theme.spacing(0.375),
                    marginLeft: 0,
                    marginRight: theme.spacing(0.25),
                    color: theme.palette.action.active
                }
            }),
            sizeMedium: ({ theme }) => ({
                height: 30,
                fontSize: theme.palette.components?.chip?.medium?.fontSize,
                fontWeight: theme.palette.components?.chip?.medium?.fontWeight,
                '& .MuiChip-icon': {
                    marginLeft: theme.spacing(1),
                    marginRight: 0
                },
                '& .MuiChip-label': {
                    paddingLeft: theme.spacing(1),
                    paddingRight: theme.spacing(1)
                },
                '& .MuiChip-deleteIcon': {
                    marginLeft: theme.spacing(0.5),
                    marginRight: theme.spacing(0.5),
                    color: theme.palette.action.active
                }
            })
        }
    }
} satisfies ThemeOptions['components'];
